import { DateTime } from "luxon";

import { IInboxConversation, IInboxPart, IInboxUser, IInboxVehicle } from "../models/messagesInbox/IInboxConversation";

// Sample conversations for the messages inbox preview, based on the agreed design
// (plans/messages-design). Nothing here is real buyer data. Times are relative to "now" so the
// reply-by countdowns look the same whenever the preview is opened.

export const SAMPLE_ME_ID = "me";

export const sampleInboxUsers: IInboxUser[] = [
  { id: "kerris", forename: "Kerris", surname: "Walden", role: "Customer service" },
  { id: "andrew", forename: "Andrew", surname: "Wallbridge", role: "Warehouse" },
  { id: "mick", forename: "Mick", surname: "Piechota", role: "Warehouse" },
];

export interface IInboxTemplate {
  name: string;
  body: string;
}

export const sampleInboxTemplates: IInboxTemplate[] = [
  {
    name: "Dispatch update",
    body: "Hi {buyer_first_name}, good news – your {part_title} was shipped with {courier}. Tracking number {tracking_no}, and it's estimated to arrive {est_delivery}.\n\nKind regards,\n{shop_name}",
  },
  {
    name: "Ask for registration",
    body: "Hi {buyer_first_name}, thanks for getting in touch. To check this part fits your car, could you reply with your vehicle registration number (or the VIN)? We'll compare it against the donor vehicle and confirm straight away.\n\nThanks,\n{shop_name}",
  },
  {
    name: "Collection",
    body: "Hi {buyer_first_name}, yes, you're welcome to collect. We're open Monday to Friday 8:30–5 and Saturday 8:30–12. Please bring your order number.\n\nKind regards,\n{shop_name}",
  },
  {
    name: "Offer – decline politely",
    body: "Hi {buyer_first_name}, thanks for the offer. We can't go that low on this one, but the listed price includes our 30-day warranty.\n\nKind regards,\n{shop_name}",
  },
];

export const SAMPLE_REG_REQUEST =
  "Hi {buyer_first_name}, thanks for getting in touch. To check this part fits your car, could you reply with your vehicle registration number (or the VIN)? We'll compare it against the donor vehicle and confirm straight away.\n\nThanks,\n{shop_name}";

const cMaxReel: IInboxPart = {
  title: "Ford C-Max MK2 2015-2019 Rear Seat Belt Reel N/S Left 1841354",
  shortTitle: "C-Max Seat Belt Reel",
  price: 24.99,
  condition: "Used – tested",
  status: "In stock",
  tagNumber: "48213",
  location: "Rack C · Bay 4 · Bin 12",
  partNumber: "F1EB-611B69-AB",
  oePartNumber: "1841354",
  photoCount: 8,
  categoryName: "Seat belts",
  aspects: [
    { name: "Placement", value: "Rear, Left" },
    { name: "Type", value: "Seat belt reel / retractor" },
    { name: "Colour", value: "Black" },
    { name: "Pretensioner", value: "Yes" },
    { name: "Warranty", value: "30 days" },
    { name: "Manufacturer part no.", value: "F1EB-611B69-AB" },
  ],
};

const cMaxDonor: IInboxVehicle = {
  vrm: "FD16KWO",
  make: "Ford",
  model: "C-Max",
  variant: "1.5 TDCi Titanium",
  series: "MK2 facelift",
  year: 2016,
  engineSize: "1499 cc",
  engineCode: "XWDB",
  fuel: "Diesel",
  gearbox: "Manual 6sp",
  colour: "Magnetic Grey",
  vinLast8: "GJ27154",
  mileage: 78420,
};

function part(overrides: Partial<IInboxPart> & Pick<IInboxPart, "title" | "shortTitle" | "price" | "tagNumber">): IInboxPart {
  return {
    condition: "Used",
    status: "In stock",
    location: "Rack A · Bay 2",
    photoCount: 6,
    categoryName: "",
    aspects: [],
    ...overrides,
  };
}

export function buildSampleConversations(now: DateTime = DateTime.utc()): IInboxConversation[] {
  const ago = (minutes: number) => now.minus({ minutes }).toISO() as string;
  const fromNow = (minutes: number) => now.plus({ minutes }).toISO() as string;
  const h = 60;
  const d = 24 * h;
  // Reply-by is 24 h after the buyer's last message.
  const replyBy = (minutesAgo: number) => fromNow(d - minutesAgo);

  return [
    {
      id: "c-daz",
      channel: "ebay",
      buyer: { username: "daz_motors88", fullName: "Darren Holt", memberSince: "2014", ordersWithUs: 3, spendWithUs: 142.5, otherOpenConversations: 0 },
      summary: "C-Max Seat Belt Reel · fits my car?",
      aiSummary: "Buyer wants to know if this N/S rear seat belt reel fits their car, reg AB16 CDE; they need the left side.",
      status: "needs_reply",
      type: "fitment",
      unread: true,
      lastBuyerMessageAt: ago(22 * h + 20),
      replyBy: fromNow(100),
      part: cMaxReel,
      donor: cMaxDonor,
      fitment: {
        reg: "AB16CDE",
        overall: "fits",
        checkedAt: ago(22 * h - 30),
        buyerVehicleSummary: "2017 Ford C-Max 1.5 TDCi",
        rows: [
          { field: "Make", buyer: "Ford", donor: "Ford", result: "match" },
          { field: "Model", buyer: "C-Max", donor: "C-Max", result: "match" },
          { field: "Series", buyer: "MK2 facelift", donor: "MK2 facelift", result: "match" },
          { field: "Model years", buyer: "2017", donor: "2015–2019", result: "match" },
          { field: "Body", buyer: "5dr MPV", donor: "5dr MPV", result: "match" },
          { field: "Engine size", buyer: "1499 cc", donor: "1499 cc", result: "match" },
          { field: "Engine code", buyer: "XWDB", donor: "XWDB", result: "match" },
          { field: "Fuel", buyer: "Diesel", donor: "Diesel", result: "match" },
          { field: "Gearbox", buyer: "Manual 6sp", donor: "Manual 6sp", result: "match" },
          { field: "Drive", buyer: "FWD", donor: "FWD", result: "match" },
        ],
      },
      messages: [
        { id: "m1", direction: "system", body: "Started from listing", sentAt: ago(22 * h + 20) },
        { id: "m2", direction: "buyer", body: "Hi, will this fit my car? Reg is AB16 CDE. It&apos;s the left side one that&apos;s gone (the belt won&apos;t retract). Cheers", sentAt: ago(22 * h + 20) },
        { id: "m3", direction: "note", authorId: "kerris", body: "@Andrew can you check 48213 in Bay 4 still has the pretensioner plug intact before I confirm?", sentAt: ago(22 * h - 13) },
        { id: "m4", direction: "note", authorId: "andrew", body: "Yep, plug is fine and belt retracts. Left it on the shelf in Bay 4.", sentAt: ago(22 * h - 29) },
        { id: "m5", direction: "system", body: "Fitment compared with donor FD16 KWO · Kerris", sentAt: ago(22 * h - 30) },
      ],
    },
    {
      id: "c-marcus",
      channel: "partpro",
      buyer: { username: "Marcus O.", fullName: "Marcus Okafor", ordersWithUs: 0, spendWithUs: 0, otherOpenConversations: 0 },
      summary: "Focus MK3 Alternator · collection?",
      aiSummary: "Buyer asks whether the alternator is still available to collect on Saturday.",
      status: "needs_reply",
      type: "collection",
      unread: true,
      lastBuyerMessageAt: ago(3 * d + 2 * h),
      replyBy: replyBy(3 * d + 2 * h),
      part: part({
        title: "Ford Focus MK3 1.6 TDCi Alternator 2011-2014 BV6N-10300-CB",
        shortTitle: "Focus MK3 Alternator",
        price: 45,
        tagNumber: "45102",
        partNumber: "BV6N-10300-CB",
        location: "Rack F · Bay 1 · Bin 3",
        categoryName: "Alternators",
      }),
      donor: { vrm: "EY13RTO", make: "Ford", model: "Focus", variant: "1.6 TDCi Zetec", year: 2013, engineSize: "1560 cc", engineCode: "T1DA", fuel: "Diesel", gearbox: "Manual 5sp", mileage: 112300 },
      messages: [
        { id: "m1", direction: "buyer", body: "Hi, is this still available for collection on Saturday?", sentAt: ago(3 * d + 2 * h) },
      ],
    },
    {
      id: "c-kev",
      channel: "ebay",
      buyer: { username: "kev.restorations", memberSince: "2009", ordersWithUs: 5, spendWithUs: 211.4, otherOpenConversations: 0 },
      summary: "Astra J Wing Mirror · £35 offer",
      aiSummary: "Buyer offers £35 including postage for the wing mirror.",
      status: "needs_reply",
      type: "offer",
      unread: false,
      lastBuyerMessageAt: ago(26 * h),
      replyBy: replyBy(26 * h),
      part: part({
        title: "Vauxhall Astra J 2010-2015 Wing Mirror Driver Side Electric Silver",
        shortTitle: "Astra J Wing Mirror",
        price: 49.99,
        tagNumber: "44871",
        oePartNumber: "13302148",
        categoryName: "Wing mirrors",
      }),
      donor: { vrm: "FN61XKA", make: "Vauxhall", model: "Astra", variant: "1.6 SRi", year: 2011, engineSize: "1598 cc", engineCode: "A16XER", fuel: "Petrol", gearbox: "Manual 5sp", colour: "Silver" },
      messages: [
        { id: "m1", direction: "buyer", body: "Any chance you can do £35 posted?", sentAt: ago(26 * h) },
      ],
    },
    {
      id: "c-sarah",
      channel: "ebay",
      buyer: { username: "sarah.k_2011", fullName: "Sarah Kenward", memberSince: "2011", ordersWithUs: 1, spendWithUs: 99.94, otherOpenConversations: 0 },
      summary: "F30 Headlight · when sent?",
      aiSummary: "Buyer ordered on Friday and is asking when the headlight will be dispatched. It shipped with DPD this morning – tracking not yet shared.",
      status: "needs_reply",
      type: "wheres_my_order",
      unread: true,
      lastBuyerMessageAt: ago(20 * h - 15),
      replyBy: fromNow(3 * h + 15),
      part: part({
        title: "BMW 3 Series F30 Headlight Halogen Passenger Side 2012-2015",
        shortTitle: "F30 Headlight",
        price: 89.99,
        tagNumber: "47902",
        oePartNumber: "7259527",
        location: "Rack A · Bay 2",
        categoryName: "Headlights",
        status: "Sold",
      }),
      donor: { vrm: "LV13HXP", make: "BMW", model: "3 Series", variant: "320d SE", series: "F30", year: 2013, engineSize: "1995 cc", engineCode: "N47D20C", fuel: "Diesel", gearbox: "Automatic 8sp" },
      order: {
        orderNumber: "12-11234-56789",
        orderedAt: ago(3 * d + 16 * h),
        pricePaid: 89.99,
        postage: 9.95,
        paymentStatus: "Paid",
        invoiceNumber: "INV-20481",
        pickedAt: ago(4 * h),
        pickedBy: "Andrew",
        labelPrintedAt: ago(3 * h),
        labelPrintedBy: "Andrew",
        shippedAt: ago(70),
        shippedBy: "Kerris",
        dispatchBy: fromNow(20 * h),
        estimatedDelivery: fromNow(28 * h),
        courier: "DPD",
        service: "Next Day",
        trackingNumber: "15501234567890",
        trackingUrl: "https://track.dpd.co.uk/parcels/15501234567890",
        deliveryName: "Sarah Kenward",
        deliveryAddress: ["14 Orchard Close", "Harrogate", "North Yorkshire", "HG2 8LP"],
      },
      messages: [
        { id: "m1", direction: "system", body: "Order placed · eBay 12-11234-56789", sentAt: ago(3 * d + 16 * h) },
        { id: "m2", direction: "system", body: "Picked by Andrew", sentAt: ago(4 * h) },
        { id: "m3", direction: "system", body: "Label printed by Andrew", sentAt: ago(3 * h) },
        { id: "m4", direction: "buyer", body: "Hi, when will this be sent? Ordered Friday and haven&apos;t had a dispatch email yet. Need it for the MOT on Thursday. Thanks, Sarah", sentAt: ago(20 * h - 15) },
        { id: "m5", direction: "system", body: "Shipped with DPD Next Day · 15501234567890", sentAt: ago(70) },
      ],
    },
    {
      id: "c-patel",
      channel: "partpro",
      buyer: { username: "J. Patel", fullName: "Jay Patel", ordersWithUs: 0, spendWithUs: 0, otherOpenConversations: 0 },
      summary: "Golf MK7 Brake Caliper · donor photo?",
      aiSummary: "Buyer asks for a photo of the donor car.",
      status: "needs_reply",
      type: "other",
      unread: false,
      lastBuyerMessageAt: ago(19 * h),
      replyBy: replyBy(19 * h),
      part: part({
        title: "VW Golf MK7 2013-2017 Front Brake Caliper Left 1.6 TDI",
        shortTitle: "Golf MK7 Brake Caliper",
        price: 32,
        tagNumber: "46230",
        categoryName: "Brake calipers",
      }),
      donor: { vrm: "WP14FRZ", make: "Volkswagen", model: "Golf", variant: "1.6 TDI Match", series: "MK7", year: 2014, engineSize: "1598 cc", engineCode: "CLHA", fuel: "Diesel", gearbox: "Manual 5sp" },
      messages: [
        { id: "m1", direction: "buyer", body: "Do you have a photo of the car it came off?", sentAt: ago(19 * h) },
      ],
    },
    {
      id: "c-tomo",
      channel: "ebay",
      buyer: { username: "tomo_1987", memberSince: "2016", ordersWithUs: 0, spendWithUs: 0, otherOpenConversations: 0 },
      summary: "Qashqai Tailgate · fit + deliver to…",
      aiSummary: "Buyer asks if the tailgate fits reg KY15 NBD and whether it can be delivered to Leeds.",
      status: "needs_reply",
      type: "fitment",
      unread: false,
      lastBuyerMessageAt: ago(15 * h),
      replyBy: replyBy(15 * h),
      part: part({
        title: "Nissan Qashqai J11 2014-2017 Tailgate Boot Lid Grey KAD",
        shortTitle: "Qashqai Tailgate",
        price: 120,
        tagNumber: "43018",
        location: "Yard · Large items",
        categoryName: "Tailgates",
      }),
      donor: { vrm: "KX14OPA", make: "Nissan", model: "Qashqai", variant: "1.5 dCi Acenta", series: "J11", year: 2014, engineSize: "1461 cc", engineCode: "K9K", fuel: "Diesel", gearbox: "Manual 6sp", colour: "Grey" },
      fitment: {
        reg: "KY15NBD",
        overall: "check",
        checkedAt: ago(14 * h),
        buyerVehicleSummary: "2015 Nissan Qashqai 1.2 DIG-T",
        note: "Same body and series. The buyer's car has a rear camera; check the tailgate has the camera cut-out.",
        rows: [
          { field: "Make", buyer: "Nissan", donor: "Nissan", result: "match" },
          { field: "Model", buyer: "Qashqai", donor: "Qashqai", result: "match" },
          { field: "Series", buyer: "J11", donor: "J11", result: "match" },
          { field: "Model years", buyer: "2015", donor: "2014–2017", result: "match" },
          { field: "Body", buyer: "5dr SUV", donor: "5dr SUV", result: "match" },
          { field: "Engine size", buyer: "1197 cc", donor: "1461 cc", result: "check" },
          { field: "Engine code", buyer: "HRA2DDT", donor: "K9K", result: "check" },
          { field: "Fuel", buyer: "Petrol", donor: "Diesel", result: "check" },
          { field: "Gearbox", buyer: "Manual 6sp", donor: "Manual 6sp", result: "match" },
          { field: "Drive", buyer: "FWD", donor: "FWD", result: "match" },
        ],
      },
      messages: [
        { id: "m1", direction: "buyer", body: "Will this fit KY15 NBD? And can you deliver to Leeds LS9?", sentAt: ago(15 * h) },
        { id: "m2", direction: "system", body: "Fitment compared with donor KX14 OPA · Kerris", sentAt: ago(14 * h) },
      ],
    },
    {
      id: "c-lucy",
      channel: "ebay",
      buyer: { username: "lucy.m.brown", fullName: "Lucy Brown", memberSince: "2018", ordersWithUs: 1, spendWithUs: 22.45, otherOpenConversations: 0 },
      summary: "Fiesta Door Lock · arrived damaged",
      aiSummary: "Buyer opened an eBay return: the door lock arrived with the actuator arm snapped and the box crushed.",
      status: "needs_reply",
      type: "return",
      unread: false,
      lastBuyerMessageAt: ago(13 * h),
      replyBy: replyBy(13 * h),
      part: part({
        title: "Ford Fiesta MK7 Door Lock Mechanism Front N/S 2008-2017",
        shortTitle: "Fiesta Door Lock",
        price: 18.5,
        tagNumber: "45511",
        categoryName: "Door locks",
        status: "Sold",
      }),
      donor: { vrm: "AJ10WMX", make: "Ford", model: "Fiesta", variant: "1.25 Zetec", series: "MK7", year: 2010, engineSize: "1242 cc", fuel: "Petrol", gearbox: "Manual 5sp" },
      order: {
        orderNumber: "12-11198-20331",
        orderedAt: ago(8 * d),
        pricePaid: 18.5,
        postage: 3.95,
        paymentStatus: "Paid",
        invoiceNumber: "INV-20377",
        pickedAt: ago(7 * d + 4 * h),
        pickedBy: "Andrew",
        labelPrintedAt: ago(7 * d + 3 * h),
        labelPrintedBy: "Andrew",
        shippedAt: ago(7 * d),
        shippedBy: "Andrew",
        dispatchBy: ago(6 * d),
        estimatedDelivery: ago(4 * d),
        courier: "Royal Mail",
        service: "Tracked 48",
        trackingNumber: "RM482913377GB",
        deliveryName: "Lucy Brown",
        deliveryAddress: ["3 Station Road", "Bedale", "DL8 1AB"],
      },
      returnCase: {
        returnId: "5234981022",
        reason: "Arrived damaged",
        state: "Awaiting your response",
        respondBy: fromNow(28 * h),
        openedAt: ago(2 * d),
        buyerReason: "Lock arrived with the actuator arm snapped off, box was crushed.",
        requested: "Full refund £18.50 + £3.95 postage",
        photoCount: 3,
        policyChecks: [
          { text: "Within 30-day return window (day 6)", result: "match" },
          { text: "Dispatch photo on file shows arm intact · Andrew", result: "match" },
          { text: "Sent Royal Mail 48, not insured · courier claim unlikely", result: "check" },
          { text: "Buyer: 1 previous order, no prior returns", result: "match" },
        ],
      },
      messages: [
        { id: "m1", direction: "system", body: "Order placed · eBay 12-11198-20331", sentAt: ago(8 * d) },
        { id: "m2", direction: "system", body: "Shipped with Royal Mail Tracked 48 · RM482913377GB", sentAt: ago(7 * d) },
        { id: "m3", direction: "system", body: "eBay return opened · Arrived damaged", sentAt: ago(2 * d) },
        { id: "m4", direction: "buyer", body: "Hi, the lock turned up with the arm snapped off and the box was crushed. I&apos;ve opened a return, photos attached on eBay.", sentAt: ago(13 * h) },
      ],
    },
    {
      id: "c-nige",
      channel: "ebay",
      buyer: { username: "nige_autos", memberSince: "2012", ordersWithUs: 4, spendWithUs: 318.4, otherOpenConversations: 0 },
      summary: "C-Max Seat Belt Reel · YD63 PLK fit?",
      aiSummary: "Buyer asks if the seat belt reel fits YD63 PLK. Their car is the pre-facelift model.",
      status: "needs_reply",
      type: "fitment",
      unread: false,
      lastBuyerMessageAt: ago(10 * h),
      replyBy: replyBy(10 * h),
      part: cMaxReel,
      donor: cMaxDonor,
      fitment: {
        reg: "YD63PLK",
        overall: "no_fit",
        checkedAt: ago(9 * h),
        buyerVehicleSummary: "2013 Ford C-Max 1.6 TDCi Zetec · T1DB",
        note: "Pre-facelift (2010–2015) reels have a different mounting and connector to the 2015–2019 part.",
        rows: [
          { field: "Make", buyer: "Ford", donor: "Ford", result: "match" },
          { field: "Model", buyer: "C-Max", donor: "C-Max", result: "match" },
          { field: "Series", buyer: "MK2 pre-facelift", donor: "MK2 facelift", result: "mismatch" },
          { field: "Model years", buyer: "2013", donor: "2015–2019", result: "mismatch" },
          { field: "Body", buyer: "5dr MPV", donor: "5dr MPV", result: "match" },
          { field: "Engine size", buyer: "1560 cc", donor: "1499 cc", result: "check" },
          { field: "Engine code", buyer: "T1DB", donor: "XWDB", result: "check" },
          { field: "Fuel", buyer: "Diesel", donor: "Diesel", result: "match" },
          { field: "Gearbox", buyer: "Manual 6sp", donor: "Manual 6sp", result: "match" },
          { field: "Drive", buyer: "FWD", donor: "FWD", result: "match" },
        ],
      },
      fitmentAlternatives: [
        { title: "Ford C-Max MK2 2010-2015 Rear Seat Belt Reel N/S Left 1708452", price: 22.99, tagNumber: "39120", listed: "Listed on eBay", listingUrl: "https://www.ebay.co.uk/itm/000000000001" },
        { title: "Ford C-Max MK2 2011-2015 Rear Seat Belt Reel N/S Left Black", price: 24.99, tagNumber: "36588", listed: "Listed on eBay + PartPro", listingUrl: "https://www.ebay.co.uk/itm/000000000002" },
        { title: "Ford Grand C-Max 2010-2015 Rear Seat Belt Reel N/S Left (7 seat)", price: 19.99, tagNumber: "41077", listed: null },
      ],
      messages: [
        { id: "m1", direction: "buyer", body: "Hi, will this fit YD63 PLK? Rear left. Thanks", sentAt: ago(10 * h) },
        { id: "m2", direction: "system", body: "Fitment compared with donor FD16 KWO · Kerris", sentAt: ago(9 * h) },
      ],
    },
    {
      id: "c-mike",
      channel: "ebay",
      buyer: { username: "mike.b.1979", fullName: "Mike Barker", memberSince: "2019", ordersWithUs: 0, spendWithUs: 0, otherOpenConversations: 0 },
      summary: "C-Max Seat Belt Reel · will it fit?",
      aiSummary: "Buyer asks if the reel fits a 2017 Focus; no reg given yet.",
      status: "waiting_on_buyer",
      type: "fitment",
      unread: false,
      lastBuyerMessageAt: ago(3 * h),
      replyBy: replyBy(3 * h),
      regRequestSentAt: ago(3 * h - 1),
      buyerVehicleDescription: "2017 Ford Focus",
      part: cMaxReel,
      donor: cMaxDonor,
      messages: [
        { id: "m1", direction: "system", body: "Started from listing", sentAt: ago(3 * h) },
        { id: "m2", direction: "buyer", body: "Hi will this fit a 2017 Focus? Need the rear left one", sentAt: ago(3 * h) },
        { id: "m3", direction: "auto", body: "Hi Mike, thanks for getting in touch. To check this part fits your car, could you reply with your vehicle registration number (or the VIN)? We'll compare it against the donor vehicle and confirm straight away.\n\nThanks,\nGP Metals", sentAt: ago(3 * h - 1) },
        { id: "m4", direction: "system", body: "Status changed to Waiting on buyer · automatic", sentAt: ago(3 * h - 1) },
      ],
    },
    {
      // Engines are matched on engine code rather than a part number.
      id: "c-engine",
      channel: "ebay",
      buyer: { username: "tdci_tony", memberSince: "2016", ordersWithUs: 0, spendWithUs: 0, otherOpenConversations: 0 },
      summary: "Focus MK3 1.6 TDCi Engine · T1DA?",
      aiSummary: "Buyer needs a replacement engine for their 2012 Focus 1.6 TDCi (reg MA62 KLT) and asks if this is a T1DA.",
      status: "needs_reply",
      type: "fitment",
      unread: true,
      lastBuyerMessageAt: ago(3 * h),
      replyBy: replyBy(3 * h),
      part: part({
        title: "Ford Focus MK3 1.6 TDCi Complete Engine T1DA 2011-2014 112k Miles",
        shortTitle: "Focus MK3 1.6 TDCi Engine",
        price: 595,
        tagNumber: "45130",
        condition: "Used – run and tested",
        location: "Engine bay 2 · Pallet 7",
        categoryName: "Engines",
        photoCount: 12,
        aspects: [
          { name: "Engine code", value: "T1DA" },
          { name: "Capacity", value: "1560 cc" },
          { name: "Fuel", value: "Diesel" },
          { name: "Mileage", value: "112,300" },
          { name: "Warranty", value: "90 days" },
        ],
      }),
      donor: { vrm: "EY13RTO", make: "Ford", model: "Focus", variant: "1.6 TDCi Zetec", series: "MK3", year: 2013, engineSize: "1560 cc", engineCode: "T1DA", fuel: "Diesel", gearbox: "Manual 5sp", gearboxCode: "IB5", mileage: 112300 },
      fitment: {
        reg: "MA62KLT",
        overall: "fits",
        checkedAt: ago(2 * h),
        buyerVehicleSummary: "2012 Ford Focus 1.6 TDCi",
        rows: [
          { field: "Make", buyer: "Ford", donor: "Ford", result: "match" },
          { field: "Model", buyer: "Focus", donor: "Focus", result: "match" },
          { field: "Series", buyer: "MK3", donor: "MK3", result: "match" },
          { field: "Model years", buyer: "2012", donor: "2011–2014", result: "match" },
          { field: "Engine size", buyer: "1560 cc", donor: "1560 cc", result: "match" },
          { field: "Engine code", buyer: "T1DA", donor: "T1DA", result: "match" },
          { field: "Fuel", buyer: "Diesel", donor: "Diesel", result: "match" },
        ],
      },
      messages: [
        { id: "m1", direction: "buyer", body: "Hi mate, is this a T1DA? Mine&apos;s a 2012 Focus 1.6 TDCi, reg MA62 KLT. Engine&apos;s let go so need a replacement", sentAt: ago(3 * h) },
        { id: "m2", direction: "system", body: "Fitment compared with donor EY13 RTO · Kerris", sentAt: ago(2 * h) },
      ],
    },
    {
      // Gearboxes are matched on gearbox code: same car, different box, so it doesn't fit.
      id: "c-gearbox",
      channel: "partpro",
      buyer: { username: "J. Whitfield", fullName: "James Whitfield", ordersWithUs: 0, spendWithUs: 0, otherOpenConversations: 0 },
      summary: "Focus MK3 Gearbox · fits YH14 ZPC?",
      aiSummary: "Buyer asks if the 5-speed gearbox fits their 2014 Focus 2.0 TDCi, reg YH14 ZPC.",
      status: "needs_reply",
      type: "fitment",
      unread: true,
      lastBuyerMessageAt: ago(5 * h),
      replyBy: replyBy(5 * h),
      part: part({
        title: "Ford Focus MK3 1.6 TDCi 5 Speed Manual Gearbox IB5 2011-2014",
        shortTitle: "Focus MK3 Gearbox",
        price: 260,
        tagNumber: "45131",
        location: "Engine bay 2 · Pallet 9",
        categoryName: "Gearbox",
        photoCount: 7,
      }),
      donor: { vrm: "EY13RTO", make: "Ford", model: "Focus", variant: "1.6 TDCi Zetec", series: "MK3", year: 2013, engineSize: "1560 cc", engineCode: "T1DA", fuel: "Diesel", gearbox: "Manual 5sp", gearboxCode: "IB5", mileage: 112300 },
      fitment: {
        reg: "YH14ZPC",
        overall: "no_fit",
        checkedAt: ago(4 * h),
        buyerVehicleSummary: "2014 Ford Focus 2.0 TDCi",
        note: "Different gearbox: the buyer's 2.0 TDCi has the 6-speed MMT6 box.",
        rows: [
          { field: "Make", buyer: "Ford", donor: "Ford", result: "match" },
          { field: "Model", buyer: "Focus", donor: "Focus", result: "match" },
          { field: "Series", buyer: "MK3", donor: "MK3", result: "match" },
          { field: "Engine size", buyer: "1997 cc", donor: "1560 cc", result: "mismatch" },
          { field: "Gearbox", buyer: "Manual 6sp", donor: "Manual 5sp", result: "mismatch" },
          { field: "Gearbox code", buyer: "MMT6", donor: "IB5", result: "mismatch" },
        ],
      },
      messages: [
        { id: "m1", direction: "buyer", body: "Hello, would this gearbox fit my Focus? YH14 ZPC. Thanks", sentAt: ago(5 * h) },
        { id: "m2", direction: "system", body: "Fitment compared with donor EY13 RTO · Kerris", sentAt: ago(4 * h) },
      ],
    },
    {
      // Cancellation by message, not picked yet: safe to cancel.
      id: "c-cancel",
      channel: "ebay",
      buyer: { username: "rachel_r", fullName: "Rachel Reid", memberSince: "2015", ordersWithUs: 0, spendWithUs: 0, otherOpenConversations: 0 },
      summary: "Golf MK7 Door Handle · cancel",
      aiSummary: "Buyer ordered the wrong door handle and wants to cancel. Not picked yet.",
      status: "needs_reply",
      type: "cancellation",
      unread: true,
      lastBuyerMessageAt: ago(35),
      replyBy: replyBy(35),
      part: part({ title: "VW Golf MK7 2013-2019 Front Door Handle Exterior Driver Side Silver", shortTitle: "Golf MK7 Door Handle", price: 22.5, tagNumber: "46120", location: "Rack D · Bay 1", categoryName: "Door handles", status: "Sold" }),
      order: {
        orderNumber: "12-11290-33410",
        orderedAt: ago(2 * h),
        pricePaid: 22.5,
        postage: 3.99,
        paymentStatus: "Paid",
        dispatchBy: fromNow(22 * h),
        courier: "Royal Mail",
        service: "Tracked 48",
        deliveryName: "Rachel Reid",
        deliveryAddress: ["3 Mill Lane", "Bristol", "BS4 2RT"],
      },
      messages: [
        { id: "m1", direction: "system", body: "Order placed · eBay 12-11290-33410", sentAt: ago(2 * h) },
        { id: "m2", direction: "buyer", body: "Hi sorry I need to cancel this, I&apos;ve ordered the wrong side", sentAt: ago(35) },
      ],
    },
    {
      // A formal eBay cancellation request, and the part is already picked: stop it via the warehouse.
      id: "c-cancel-ebay",
      channel: "ebay",
      buyer: { username: "jjb_motors", memberSince: "2012", ordersWithUs: 4, spendWithUs: 188.2, otherOpenConversations: 0 },
      summary: "Corsa E Wiper Motor · eBay cancel request",
      aiSummary: "Buyer opened an eBay cancellation request (bought by mistake). The part was picked by Andrew this morning.",
      status: "needs_reply",
      type: "cancellation",
      unread: true,
      lastBuyerMessageAt: ago(50),
      replyBy: replyBy(50),
      ebayCancelRequest: { openedAt: ago(50), reason: "Buyer ordered by mistake" },
      part: part({ title: "Vauxhall Corsa E 2014-2019 Front Wiper Motor and Linkage", shortTitle: "Corsa E Wiper Motor", price: 34.99, tagNumber: "45877", location: "Rack E · Bay 3", categoryName: "Wiper motors", status: "Sold" }),
      order: {
        orderNumber: "12-11288-90122",
        orderedAt: ago(14 * h),
        pricePaid: 34.99,
        postage: 4.99,
        paymentStatus: "Paid",
        pickedAt: ago(2 * h),
        pickedBy: "Andrew",
        dispatchBy: fromNow(8 * h),
        courier: "DPD",
        service: "Next Day",
        deliveryName: "J Bennett",
        deliveryAddress: ["Unit 4, Brook Ind. Est.", "Leeds", "LS11 5QT"],
      },
      messages: [
        { id: "m1", direction: "system", body: "Order placed · eBay 12-11288-90122", sentAt: ago(14 * h) },
        { id: "m2", direction: "system", body: "Picked by Andrew", sentAt: ago(2 * h) },
        { id: "m3", direction: "system", body: "eBay cancellation request opened · Buyer ordered by mistake", sentAt: ago(50) },
      ],
    },
    {
      // Wrong address, label already printed: change it and reprint before the courier collects.
      id: "c-address",
      channel: "partpro",
      buyer: { username: "S. Ahmed", fullName: "Sami Ahmed", ordersWithUs: 1, spendWithUs: 64, otherOpenConversations: 0 },
      summary: "Qashqai Radiator · wrong address",
      aiSummary: "Buyer put their home address but wants it sent to their work instead. Label already printed.",
      status: "needs_reply",
      type: "address_change",
      unread: true,
      lastBuyerMessageAt: ago(20),
      replyBy: replyBy(20),
      part: part({ title: "Nissan Qashqai J11 1.5 dCi Radiator 2014-2017", shortTitle: "Qashqai Radiator", price: 64, tagNumber: "46301", location: "Rack G · Bay 2", categoryName: "Radiators", status: "Sold" }),
      order: {
        orderNumber: "PP-30418",
        orderedAt: ago(5 * h),
        pricePaid: 64,
        postage: 8.5,
        paymentStatus: "Paid",
        pickedAt: ago(3 * h),
        pickedBy: "Mick",
        labelPrintedAt: ago(2 * h),
        labelPrintedBy: "Mick",
        dispatchBy: fromNow(4 * h),
        courier: "DPD",
        service: "Next Day",
        deliveryName: "Sami Ahmed",
        deliveryAddress: ["22 Ash Grove", "Leicester", "LE2 6PL"],
      },
      messages: [
        { id: "m1", direction: "system", body: "Order placed · PartPro PP-30418", sentAt: ago(5 * h) },
        { id: "m2", direction: "system", body: "Picked by Mick", sentAt: ago(3 * h) },
        { id: "m3", direction: "system", body: "Label printed by Mick", sentAt: ago(2 * h) },
        { id: "m4", direction: "buyer", body: "Hiya I&apos;ve put the wrong address on, can it go to my work instead please. Won&apos;t be in at home", sentAt: ago(20) },
      ],
    },
    {
      // Address change with the new address in the message: AI reads it out, ready to update.
      id: "c-address-given",
      channel: "ebay",
      buyer: { username: "tom_r_parts", fullName: "Tom Rushton", memberSince: "2016", ordersWithUs: 3, spendWithUs: 141, otherOpenConversations: 0 },
      summary: "Fiesta MK7 Rear Light · new address",
      aiSummary: "Buyer moved house and gave the new address: 41 Park Road, Sheffield, S10 2BT. Not picked yet.",
      status: "needs_reply",
      type: "address_change",
      unread: true,
      lastBuyerMessageAt: ago(15),
      replyBy: replyBy(15),
      newAddress: ["41 Park Road", "Sheffield", "S10 2BT"],
      part: part({ title: "Ford Fiesta MK7 2008-2012 Rear Light Passenger Side", shortTitle: "Fiesta MK7 Rear Light", price: 18.5, tagNumber: "46550", location: "Rack C · Bay 2", categoryName: "Rear lights", status: "Sold" }),
      order: {
        orderNumber: "12-11295-10870",
        orderedAt: ago(h),
        pricePaid: 18.5,
        postage: 3.99,
        paymentStatus: "Paid",
        dispatchBy: fromNow(23 * h),
        courier: "Royal Mail",
        service: "Tracked 48",
        deliveryName: "Tom Rushton",
        deliveryAddress: ["17 Hill Street", "Rotherham", "S60 1QT"],
      },
      messages: [
        { id: "m1", direction: "system", body: "Order placed · eBay 12-11295-10870", sentAt: ago(h) },
        { id: "m2", direction: "buyer", body: "Hi, just moved and forgot to update eBay. Can you send it to 41 Park Road, Sheffield S10 2BT instead? Cheers", sentAt: ago(15) },
      ],
    },
    {
      // Shipped six days ago and two days past its estimated delivery: flagged as late.
      id: "c-late",
      channel: "ebay",
      buyer: { username: "kwik_fit_kev", memberSince: "2013", ordersWithUs: 2, spendWithUs: 96, otherOpenConversations: 0 },
      summary: "Transit Custom Turbo Pipe · not arrived",
      aiSummary: "Buyer says the turbo pipe hasn't arrived. It shipped with Evri six days ago and was due two days ago.",
      status: "needs_reply",
      type: "wheres_my_order",
      unread: true,
      lastBuyerMessageAt: ago(4 * h),
      replyBy: replyBy(4 * h),
      part: part({ title: "Ford Transit Custom 2.2 TDCi Intercooler Turbo Pipe 2012-2016", shortTitle: "Transit Turbo Pipe", price: 38, tagNumber: "45512", location: "Rack H · Bay 4", categoryName: "Turbo pipes", status: "Sold" }),
      order: {
        orderNumber: "12-11201-77310",
        orderedAt: ago(7 * d),
        pricePaid: 38,
        postage: 6.5,
        paymentStatus: "Paid",
        pickedAt: ago(6 * d + 4 * h),
        pickedBy: "Andrew",
        labelPrintedAt: ago(6 * d + 3 * h),
        labelPrintedBy: "Andrew",
        shippedAt: ago(6 * d + 2 * h),
        shippedBy: "Andrew",
        dispatchBy: ago(6 * d),
        estimatedDelivery: ago(2 * d + 2 * h),
        courier: "Evri",
        service: "48 Hour",
        trackingNumber: "H01HYA0012345678",
        trackingUrl: "https://www.evri.com/track/parcel/H01HYA0012345678",
        deliveryName: "Kevin Doyle",
        deliveryAddress: ["Doyle Motors", "9 Station Road", "Wigan", "WN1 2DA"],
      },
      messages: [
        { id: "m1", direction: "system", body: "Order placed · eBay 12-11201-77310", sentAt: ago(7 * d) },
        { id: "m2", direction: "system", body: "Shipped with Evri 48 Hour · H01HYA0012345678", sentAt: ago(6 * d + 2 * h) },
        { id: "m3", direction: "buyer", body: "Still not turned up mate, tracking hasn&apos;t moved in days", sentAt: ago(4 * h) },
      ],
    },
    {
      // Fitment question with the car described but no reg or VIN: ask for one.
      id: "c-focus",
      channel: "ebay",
      buyer: { username: "lee_focus_st", memberSince: "2021", ordersWithUs: 0, spendWithUs: 0, otherOpenConversations: 0 },
      summary: "Focus MK3 Alternator · fits 2012 Focus?",
      aiSummary: "Buyer asks if the alternator fits their 2012 Ford Focus 1.6 diesel. No reg or VIN given.",
      status: "needs_reply",
      type: "fitment",
      unread: true,
      lastBuyerMessageAt: ago(25),
      replyBy: replyBy(25),
      buyerVehicleDescription: "2012 Ford Focus 1.6 diesel",
      part: part({
        title: "Ford Focus MK3 1.6 TDCi Alternator 2011-2014 BV6N-10300-CB",
        shortTitle: "Focus MK3 Alternator",
        price: 45,
        tagNumber: "45102",
        partNumber: "BV6N-10300-CB",
        location: "Rack F · Bay 1 · Bin 3",
        categoryName: "Alternators",
      }),
      donor: { vrm: "EY13RTO", make: "Ford", model: "Focus", variant: "1.6 TDCi Zetec", series: "MK3", year: 2013, engineSize: "1560 cc", engineCode: "T1DA", fuel: "Diesel", gearbox: "Manual 5sp", gearboxCode: "IB5", mileage: 112300 },
      messages: [
        { id: "m1", direction: "system", body: "Started from listing", sentAt: ago(25) },
        { id: "m2", direction: "buyer", body: "Will this fit my 2012 Ford Focus 1.6 diesel?", sentAt: ago(25) },
      ],
    },
    {
      // eBay "item not received" inquiry: tracking shows it's on its way.
      id: "c-inr",
      channel: "ebay",
      buyer: { username: "brakes_r_us", memberSince: "2010", ordersWithUs: 6, spendWithUs: 312.4, otherOpenConversations: 0 },
      summary: "Astra K Brake Caliper · not received case",
      aiSummary: "Buyer opened an eBay item-not-received request. Shipped with DPD 3 days ago; estimated delivery was yesterday.",
      status: "needs_reply",
      type: "wheres_my_order",
      unread: true,
      lastBuyerMessageAt: ago(6 * h),
      replyBy: replyBy(6 * h),
      ebayCase: { caseId: "5312004871", kind: "item_not_received", stage: "awaiting_seller", openedAt: ago(6 * h), respondBy: fromNow(2 * d + 18 * h), reason: "Item not received", buyerWants: "refund" },
      part: part({ title: "Vauxhall Astra K 2015-2019 Front Brake Caliper Left 1.6 CDTi", shortTitle: "Astra K Brake Caliper", price: 42, tagNumber: "45610", location: "Rack B · Bay 5", categoryName: "Brake calipers", status: "Sold" }),
      order: {
        orderNumber: "12-11250-44021",
        orderedAt: ago(4 * d),
        pricePaid: 42,
        postage: 6.95,
        paymentStatus: "Paid",
        pickedAt: ago(3 * d + 5 * h),
        pickedBy: "Andrew",
        labelPrintedAt: ago(3 * d + 4 * h),
        labelPrintedBy: "Andrew",
        shippedAt: ago(3 * d + 3 * h),
        shippedBy: "Andrew",
        dispatchBy: ago(3 * d),
        estimatedDelivery: ago(d),
        courier: "DPD",
        service: "Next Day",
        trackingNumber: "15501234599911",
        trackingUrl: "https://track.dpd.co.uk/parcels/15501234599911",
        deliveryName: "Brakes R Us Ltd",
        deliveryAddress: ["Unit 2, Canal Works", "Stoke-on-Trent", "ST4 4AB"],
      },
      messages: [
        { id: "m1", direction: "system", body: "Shipped with DPD Next Day · 15501234599911", sentAt: ago(3 * d + 3 * h) },
        { id: "m2", direction: "system", body: "eBay: buyer opened an item not received request · case 5312004871", sentAt: ago(6 * h) },
        { id: "m3", direction: "buyer", body: "Hasn&apos;t arrived and DPD tracking says delayed. Need it for a job Friday", sentAt: ago(6 * h) },
      ],
    },
    {
      // eBay "not as described" case, escalated: damaged in transit, a partial refund may settle it.
      id: "c-snad",
      channel: "ebay",
      buyer: { username: "classic_mini_matt", fullName: "Matt Owens", memberSince: "2008", ordersWithUs: 1, spendWithUs: 120, otherOpenConversations: 0 },
      summary: "Range Rover Sport Headlight · damaged case",
      aiSummary: "Headlight arrived with a cracked mounting tab. Buyer says it still works and would keep it for a partial refund. eBay has stepped in.",
      status: "needs_reply",
      type: "return",
      unread: true,
      lastBuyerMessageAt: ago(3 * h),
      replyBy: replyBy(3 * h),
      ebayCase: { caseId: "5311987650", kind: "not_as_described", stage: "escalated", openedAt: ago(4 * d), respondBy: fromNow(20 * h), reason: "Arrived damaged", buyerWants: "refund" },
      part: part({ title: "Range Rover Sport L494 2013-2017 Headlight Xenon Driver Side", shortTitle: "Range Rover Sport Headlight", price: 120, tagNumber: "45001", location: "Rack A · Bay 7", categoryName: "Headlights", status: "Sold" }),
      order: {
        orderNumber: "12-11180-20455",
        orderedAt: ago(10 * d),
        pricePaid: 120,
        postage: 12.5,
        paymentStatus: "Paid",
        pickedAt: ago(9 * d),
        pickedBy: "Lewis",
        labelPrintedAt: ago(9 * d - 1 * h),
        labelPrintedBy: "Lewis",
        shippedAt: ago(9 * d - 2 * h),
        shippedBy: "Lewis",
        dispatchBy: ago(9 * d),
        estimatedDelivery: ago(7 * d),
        courier: "Proovia",
        service: "Large item",
        trackingNumber: "PRV88213",
        deliveryName: "Matt Owens",
        deliveryAddress: ["8 Fern Close", "Swindon", "SN3 1QQ"],
      },
      messages: [
        { id: "m1", direction: "system", body: "eBay: buyer opened a not as described case · Arrived damaged · case 5311987650", sentAt: ago(4 * d) },
        { id: "m2", direction: "buyer", body: "One of the mounting tabs is snapped off, box was crushed. Light works fine, I can glue it. Would keep it for some money back", sentAt: ago(4 * d) },
        { id: "m3", direction: "us", authorId: "kerris", body: "Sorry to hear that Matt – could you send a photo of the tab and the box please?", sentAt: ago(3 * d) },
        { id: "m4", direction: "system", body: "eBay stepped in: case escalated · eBay will decide if there's no agreement", sentAt: ago(d) },
        { id: "m5", direction: "buyer", body: "Photos are on the eBay case. Happy with £30 back", sentAt: ago(3 * h) },
      ],
    },
    {
      id: "c-gary",
      channel: "ebay",
      buyer: { username: "gazza_r53", memberSince: "2020", ordersWithUs: 0, spendWithUs: 0, otherOpenConversations: 0 },
      summary: "C-Max Seat Belt Reel · fits WR17 TXN?",
      aiSummary: "Buyer gives reg WR17 TXN and asks if the reel fits.",
      status: "needs_reply",
      type: "fitment",
      unread: true,
      lastBuyerMessageAt: ago(40),
      replyBy: replyBy(40),
      part: cMaxReel,
      donor: cMaxDonor,
      messages: [
        { id: "m1", direction: "buyer", body: "Hiya, does this fit WR17 TXN? It&apos;s the passenger side rear", sentAt: ago(40) },
      ],
    },
    {
      id: "c-paul",
      channel: "ebay",
      buyer: { username: "paul.s.motors", memberSince: "2015", ordersWithUs: 2, spendWithUs: 104, otherOpenConversations: 0 },
      summary: 'A3 Front Wing · "Thanks mate"',
      status: "needs_reply",
      type: "other",
      unread: false,
      lastBuyerMessageAt: ago(4 * h),
      replyBy: replyBy(4 * h),
      part: part({ title: "Audi A3 8V Front Wing Driver Side Black LY9B", shortTitle: "A3 Front Wing", price: 65, tagNumber: "42210", categoryName: "Wings", status: "Sold" }),
      order: {
        orderNumber: "12-11201-77310",
        orderedAt: ago(2 * d),
        pricePaid: 65,
        postage: 14.95,
        paymentStatus: "Paid",
        pickedAt: ago(d + 6 * h),
        pickedBy: "Mick",
        shippedAt: ago(d + 2 * h),
        shippedBy: "Mick",
        dispatchBy: ago(d),
        estimatedDelivery: fromNow(6 * h),
        courier: "Proovia",
        service: "2-man",
        trackingNumber: "PRV-559201",
        deliveryName: "Paul Stevens",
        deliveryAddress: ["Unit 4, Mill Lane", "Wakefield", "WF1 2QQ"],
      },
      messages: [
        { id: "m1", direction: "us", authorId: "kerris", body: "Hi Paul, the wing went out with Proovia yesterday and should be with you today.", sentAt: ago(5 * h) },
        { id: "m2", direction: "buyer", body: "Thanks mate", sentAt: ago(4 * h) },
      ],
    },
    {
      id: "c-helen",
      channel: "ebay",
      buyer: { username: "helen_w22", memberSince: "2021", ordersWithUs: 1, spendWithUs: 54.99, otherOpenConversations: 0 },
      summary: 'Yaris Alternator · "ok"',
      status: "needs_reply",
      type: "other",
      unread: false,
      lastBuyerMessageAt: ago(2 * h),
      replyBy: replyBy(2 * h),
      part: part({ title: "Toyota Yaris MK3 1.33 Alternator 2011-2017", shortTitle: "Yaris Alternator", price: 54.99, tagNumber: "44012", categoryName: "Alternators", status: "Sold" }),
      order: {
        orderNumber: "12-11240-10022",
        orderedAt: ago(5 * h),
        pricePaid: 54.99,
        postage: 0,
        paymentStatus: "Paid",
        dispatchBy: fromNow(d),
        courier: "Royal Mail",
        service: "Tracked 48",
        deliveryName: "Helen Wright",
        deliveryAddress: ["22 Kings Road", "York", "YO1 7HH"],
      },
      messages: [
        { id: "m1", direction: "system", body: "Order placed · eBay 12-11240-10022", sentAt: ago(5 * h) },
        { id: "m2", direction: "us", authorId: "kerris", body: "Thanks Helen, this will go out tomorrow.", sentAt: ago(3 * h) },
        { id: "m3", direction: "buyer", body: "ok", sentAt: ago(2 * h) },
      ],
    },
    {
      // Starts with thanks but needs a reply: the "no reply needed" check must leave it alone.
      id: "c-dan",
      channel: "ebay",
      buyer: { username: "dan_the_van", fullName: "Dan Mercer", memberSince: "2018", ordersWithUs: 2, spendWithUs: 74.98, otherOpenConversations: 0 },
      summary: 'Transit Mirror Glass · "wrong side"',
      aiSummary: "Buyer thanks us for the quick delivery but says the mirror glass is for the wrong side.",
      status: "needs_reply",
      type: "other",
      unread: true,
      lastBuyerMessageAt: ago(90),
      replyBy: replyBy(90),
      part: part({ title: "Ford Transit Custom 2013-2018 Wing Mirror Glass Heated Passenger Side", shortTitle: "Transit Mirror Glass", price: 19.99, tagNumber: "44390", categoryName: "Wing mirror glass", status: "Sold" }),
      messages: [
        { id: "m1", direction: "us", body: "Hi Dan, your mirror glass is on its way with Royal Mail.", sentAt: ago(d + 2 * h) },
        { id: "m2", direction: "buyer", body: "Thanks, but it&apos;s the wrong side", sentAt: ago(90) },
      ],
    },
    {
      id: "c-hughes",
      channel: "partpro",
      buyer: { username: "R. Hughes", fullName: "Rhys Hughes", ordersWithUs: 1, spendWithUs: 285, otherOpenConversations: 0 },
      summary: "308 Turbo · collect this afternoon?",
      aiSummary: "Buyer has paid and asks to collect this afternoon instead of delivery.",
      status: "needs_reply",
      type: "collection",
      unread: true,
      lastBuyerMessageAt: ago(8 * h),
      replyBy: replyBy(8 * h),
      part: part({ title: "Peugeot 308 T9 1.6 BlueHDi Turbocharger 2014-2018", shortTitle: "308 Turbo", price: 285, tagNumber: "46877", categoryName: "Turbochargers", status: "Sold" }),
      order: {
        orderNumber: "PP-104882",
        orderedAt: ago(9 * h),
        pricePaid: 285,
        postage: 12,
        paymentStatus: "Paid",
        dispatchBy: fromNow(15 * h),
        courier: "DPD",
        service: "Next Day",
        deliveryName: "Rhys Hughes",
        deliveryAddress: ["8 Elm Grove", "Ripon", "HG4 1JD"],
        buyerVehicleReg: "YH15ZPD",
      },
      messages: [
        { id: "m1", direction: "system", body: "Order placed · PartPro PP-104882", sentAt: ago(9 * h) },
        { id: "m2", direction: "buyer", body: "Can I collect this afternoon instead of delivery?", sentAt: ago(8 * h) },
      ],
    },
    {
      id: "c-ben",
      channel: "ebay",
      buyer: { username: "bennyb_parts", memberSince: "2013", ordersWithUs: 6, spendWithUs: 402.1, otherOpenConversations: 0 },
      summary: "Transit Custom Mirror · trade price?",
      status: "needs_reply",
      type: "offer",
      unread: false,
      lastBuyerMessageAt: ago(6 * h),
      replyBy: replyBy(6 * h),
      part: part({ title: "Ford Transit Custom 2013-2018 Door Mirror Passenger Side Manual", shortTitle: "Transit Custom Mirror", price: 59.99, tagNumber: "40991", categoryName: "Wing mirrors" }),
      messages: [
        { id: "m1", direction: "buyer", body: "Do you do a trade price if I take three?", sentAt: ago(6 * h) },
        { id: "m2", direction: "note", authorId: "kerris", body: "@Adam can we do a trade price on three of these? He's had 6 orders with us.", sentAt: ago(5 * h) },
      ],
    },
    {
      id: "c-carl",
      channel: "ebay",
      buyer: { username: "clio_carl", memberSince: "2017", ordersWithUs: 1, spendWithUs: 18, otherOpenConversations: 0 },
      summary: "Clio Coil Pack · tracking?",
      status: "waiting_on_buyer",
      type: "wheres_my_order",
      unread: false,
      lastBuyerMessageAt: ago(d + 3 * h),
      replyBy: replyBy(d + 3 * h),
      part: part({ title: "Renault Clio MK4 0.9 TCe Ignition Coil Pack", shortTitle: "Clio Coil Pack", price: 18, tagNumber: "43555", categoryName: "Ignition", status: "Sold" }),
      messages: [
        { id: "m1", direction: "buyer", body: "Has this been posted yet?", sentAt: ago(d + 3 * h) },
        { id: "m2", direction: "us", authorId: "kerris", body: "Yes, it went out with Royal Mail this morning – tracking is on the order.", sentAt: ago(d + 2 * h) },
      ],
    },
  ];
}
